import { WaterMarkSvg } from 'businessCommon/src/components/@waterMark/waterMarkSvg';
import type { IWaterMarkProps } from '@umu/umu-types';
import { useAsyncEffect } from 'ahooks';
import React from 'react';
import { useEffect, useState } from 'react';
import { getWaterMarkText } from '../utils/common';

interface IProps {
    defaultText: string;
    isFullscreen: boolean;
    scale?: number;
    waterMarkOptions?: IWaterMarkProps;
}

interface IConfigInfo {
    isLoading: boolean;
    info: IWaterMarkProps;
}

/** 水印区域尺寸与定位：文档小于容器且居中时，需 left/top 偏移使水印覆盖文档（与垂直/水平模式无关，两种布局都会居中） */
interface WatermarkDimensions {
    width?: number;
    height?: number;
    left?: number;
    top?: number;
}

/**
 * 更新水印尺寸的回调函数类型
 */
type OnDimensionsUpdateCallback = (dimensions: WatermarkDimensions | null) => void;

/**
 * 设置水印尺寸监听器的参数
 */
interface SetupWatermarkResizeObserverParams {
    onDimensionsUpdate: OnDimensionsUpdateCallback;
}

/**
 * 设置水印尺寸监听器的返回值
 */
interface SetupWatermarkResizeObserverReturn {
    cleanup: () => void;
}

/**
 * 立即更新水印尺寸
 */
function updateWatermarkDimensions(onDimensionsUpdate: OnDimensionsUpdateCallback): void {
    if (!onDimensionsUpdate) {
        return;
    }
    const dimensions = getWatermarkDimensions();
    onDimensionsUpdate(dimensions);
}

/**
 * 延迟更新水印尺寸，确保 DOM 更新完成（特别是全屏切换时）
 */
function updateWatermarkDimensionsWithDelay(onDimensionsUpdate: OnDimensionsUpdateCallback): void {
    if (!onDimensionsUpdate) {
        return;
    }
    // 使用 requestAnimationFrame 确保在下一帧获取尺寸
    requestAnimationFrame(() => {
        // 再延迟一帧，确保全屏切换后的布局完全更新
        requestAnimationFrame(() => {
            updateWatermarkDimensions(onDimensionsUpdate);
        });
    });
}

/**
 * 设置 ResizeObserver 监听布局元素
 */
function setupResizeObserver(resizeObserver: ResizeObserver, onDimensionsUpdate: OnDimensionsUpdateCallback): void {
    if (!(resizeObserver && onDimensionsUpdate)) {
        return;
    }

    const verticalList = document.querySelector('.pdf-list-vertical');
    const horizontalList = document.querySelector('.pdf-list-horizontal');

    if (verticalList) {
        resizeObserver.observe(verticalList);
        // 也监听 list-item 的宽度变化
        const listItem = document.querySelector('.pdf-viewport-list-item');
        if (listItem) {
            resizeObserver.observe(listItem);
        }
    }

    if (horizontalList) {
        resizeObserver.observe(horizontalList);
        // 也监听 page-container 的尺寸变化
        const pageContainer = horizontalList.querySelector('.pdf-page-container');
        if (pageContainer) {
            resizeObserver.observe(pageContainer);
        }
    }
}

/**
 * 设置水印尺寸监听器
 * 包括 ResizeObserver 和延迟更新机制
 */
function setupWatermarkResizeObserver(params: SetupWatermarkResizeObserverParams): SetupWatermarkResizeObserverReturn {
    if (!params?.onDimensionsUpdate) {
        return {
            cleanup: () => {
                // 空清理函数
            },
        };
    }

    const { onDimensionsUpdate } = params;

    // 延迟获取初始尺寸，确保 DOM 已更新
    updateWatermarkDimensionsWithDelay(onDimensionsUpdate);

    // 检查 ResizeObserver 是否可用（SSR 或不支持的环境中可能不存在）
    if (typeof ResizeObserver === 'undefined') {
        return {
            cleanup: () => {
                // 空清理函数
            },
        };
    }

    // 创建 ResizeObserver 监听布局变化
    const resizeObserver = new ResizeObserver(() => {
        updateWatermarkDimensions(onDimensionsUpdate);
    });

    // 延迟设置 ResizeObserver，确保 DOM 已更新（特别是全屏切换时）
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            setupResizeObserver(resizeObserver, onDimensionsUpdate);
        });
    });

    return {
        cleanup: () => {
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
        },
    };
}

/**
 * 获取水印区域尺寸与定位（文档全尺寸，不钳位）
 * 当文档宽度/高度小于容器且布局居中时，返回 left/top 使水印层与文档对齐（PdfViewport 中垂直与水平模式均可能居中文档）。
 */
function getWatermarkDimensions(): WatermarkDimensions | null {
    const verticalList = document.querySelector('.pdf-list-vertical') as HTMLElement;
    if (verticalList) {
        const height = verticalList.clientHeight;
        const listItem = document.querySelector('.pdf-viewport-list-item') as HTMLElement;
        if (listItem) {
            const cw = verticalList.clientWidth;
            const nw = listItem.clientWidth;
            const left = cw > nw ? Math.floor((cw - nw) / 2) : 0;
            return { width: nw, height, left, top: 0 };
        }
        return { height };
    }

    const horizontalList = document.querySelector('.pdf-list-horizontal') as HTMLElement;
    if (horizontalList) {
        const pageContainer = horizontalList.querySelector('.pdf-page-container') as HTMLElement;
        if (pageContainer) {
            const listRect = horizontalList.getBoundingClientRect();
            const pageRect = pageContainer.getBoundingClientRect();
            const left = Math.round(pageRect.left - listRect.left + horizontalList.scrollLeft);
            const top = Math.round(pageRect.top - listRect.top + horizontalList.scrollTop);
            return {
                width: pageContainer.clientWidth,
                height: pageContainer.clientHeight,
                left,
                top,
            };
        }
        return {
            width: horizontalList.clientWidth,
            height: horizontalList.clientHeight,
        };
    }

    return null;
}

export function WaterMarkWrap(props: IProps) {
    const { waterMarkOptions, defaultText, isFullscreen, scale = 1 } = props;
    const [configInfo, setConfigInfo] = useState<IConfigInfo>({
        isLoading: true,
        info: {
            text: defaultText,
            ...waterMarkOptions,
        },
    });
    const [watermarkDimensions, setWatermarkDimensions] = useState<WatermarkDimensions | null>(null);

    const onInit = async () => {
        const data = await getWaterMarkText();
        setConfigInfo((prev) => ({
            ...prev,
            isLoading: false,
            info: {
                ...prev.info,
                ...data,
            },
        }));
    };

    useAsyncEffect(async () => {
        await onInit();
    }, []);

    // 监听 scale、isFullscreen 变化和布局变化，更新水印定位
    useEffect(() => {
        if (configInfo.isLoading) {
            return;
        }
        const { cleanup } = setupWatermarkResizeObserver({
            onDimensionsUpdate: setWatermarkDimensions,
        });
        return cleanup;
    }, [configInfo.isLoading, scale, isFullscreen]);

    if (configInfo.isLoading) {
        return null;
    }

    const fullWidth = watermarkDimensions?.width;
    const fullHeight = watermarkDimensions?.height;
    const left = watermarkDimensions?.left ?? 0;
    const top = watermarkDimensions?.top ?? 0;

    // overflow: hidden 裁剪 WaterMark 内部 inset:-100% 的旋转覆盖层
    // 外层用文档全尺寸与 left/top 偏移，保证水印覆盖文档（文档小于容器且居中时偏移非 0）
    const wrapperStyle: React.CSSProperties | undefined =
        fullWidth != null && fullHeight != null && (fullWidth > 0 || fullHeight > 0)
            ? {
                position: 'absolute',
                left,
                top,
                width: fullWidth,
                height: fullHeight,
                overflow: 'hidden',
                pointerEvents: 'none',
            }
            : undefined;

    return (
        <div className="watermark-wrap-outer" style={wrapperStyle ?? {}}>
            <WaterMarkSvg {...configInfo.info} isShowWaterMark isFullscreen={isFullscreen} />
        </div>
    );
}
